import { useState } from 'react';
import { Field } from './ui';
import { assetUrl } from '../api/client';
import { mediaApi } from '../api/resources';
import { kindForFile } from '../lib/media';
import { detectVideoProvider, providerLabel } from '../lib/videoProvider';
import { nextGalleryKey } from '../lib/gallery';
import { useToast } from '../context/ToastContext';

/**
 * Reusable gallery editor shared by the News forms (admin + member). This is a
 * faithful port of the inline gallery manager in AdminActivities: upload images
 * and videos, add a YouTube or Facebook video link, reorder with arrows, and
 * remove items.
 *
 * The parent owns the `items` array and the dirty flag. `onChange(updater)` is
 * always called with a functional updater `(prev) => next` so multi-file
 * uploads append safely across awaits; the parent applies it and marks the
 * gallery dirty. Per-item validation errors (from a 422) are read off the items.
 *
 * Props:
 *   items              current gallery rows
 *   onChange           (prev => next) => void, marks the gallery dirty
 *   disabled           disable every control (while the form is saving)
 *   onUploadingChange  optional (bool) => void so the parent can block Save
 */
export default function GalleryEditor({ items = [], onChange, disabled = false, onUploadingChange }) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState('');

  function setUploadingState(value) {
    setUploading(value);
    onUploadingChange?.(value);
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingState(true);
    try {
      for (const file of files) {
        const kind = kindForFile(file); // 'image' | 'video' | 'document'
        if (kind === 'document') {
          toast.error(`${file.name}: only images and videos can be added to the gallery.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        fd.append('alt_text', file.name);
        const media = await mediaApi.upload(fd);
        // Read from `media.url` / `media.id` (never `media.path`).
        if (media?.id == null) throw new Error('Upload succeeded but no media id was returned.');
        onChange((prev) => [
          ...prev,
          {
            key: nextGalleryKey(),
            kind,
            url: media.url,
            embed_url: null,
            provider: null,
            alt_text: media.alt_text || file.name,
            mediaId: media.id,
            error: '',
          },
        ]);
      }
      toast.success('Media uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploadingState(false);
      e.target.value = '';
    }
  }

  function addVideoLink() {
    const url = linkInput.trim();
    if (!url) return;
    const provider = detectVideoProvider(url);
    if (!provider) {
      setLinkError('Only YouTube or Facebook video links, or public Facebook post links, are allowed.');
      return;
    }
    onChange((prev) => [
      ...prev,
      {
        key: nextGalleryKey(),
        kind: 'embed',
        url,
        embed_url: null,
        provider,
        alt_text: '',
        mediaId: null,
        error: '',
      },
    ]);
    setLinkInput('');
    setLinkError('');
  }

  function removeItem(key) {
    onChange((prev) => prev.filter((g) => g.key !== key));
  }

  function moveItem(index, delta) {
    onChange((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  return (
    <div className="space-y-4 border-t border-cream-300 pt-5">
      <div>
        <h3 className="font-display text-base font-semibold text-ink">Gallery</h3>
        <p className="text-sm text-ink-muted">
          Add more photos to show alongside this post. Upload images or videos, or paste a
          YouTube or Facebook video link, or a public Facebook post link. Use the arrows to set the display order.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Upload photos / videos" hint="Images and MP4 or WebM video.">
          <input
            type="file"
            accept="image/*,video/mp4,video/webm"
            multiple
            onChange={handleUpload}
            className="text-sm"
            disabled={disabled || uploading}
          />
          {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
        </Field>

        <Field label="Add video or Facebook post link" error={linkError} hint="YouTube or Facebook video URL, or a public Facebook post URL.">
          <div className="flex gap-2">
            <input
              className="input"
              value={linkInput}
              onChange={(e) => {
                setLinkInput(e.target.value);
                if (linkError) setLinkError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addVideoLink();
                }
              }}
              placeholder="https://www.youtube.com/watch?v=…"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={addVideoLink}
              className="btn-outline shrink-0"
              disabled={disabled}
            >
              Add
            </button>
          </div>
        </Field>
      </div>

      {items.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {items.map((g, i) => (
            <li
              key={g.key}
              className={`flex items-center gap-3 rounded-xl border bg-surface-alt p-2 ${
                g.error ? 'border-red-400' : 'border-cream-300'
              }`}
            >
              <GalleryThumb item={g} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-soft">
                  {g.kind === 'embed'
                    ? providerLabel(g.provider)
                    : g.kind === 'video'
                      ? 'Video'
                      : g.alt_text || `Photo ${i + 1}`}
                </p>
                {g.kind === 'embed' && <p className="truncate text-xs text-ink-muted">{g.url}</p>}
                {g.error && <p className="mt-0.5 text-xs text-red-600">{g.error}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(i, -1)}
                  disabled={disabled || i === 0}
                  aria-label="Move up"
                  className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(i, 1)}
                  disabled={disabled || i === items.length - 1}
                  aria-label="Move down"
                  className="btn-ghost px-2 py-1 text-xs disabled:opacity-40"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(g.key)}
                  disabled={disabled}
                  aria-label="Remove item"
                  className="btn-ghost px-2 py-1 text-xs text-red-600"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GalleryThumb({ item }) {
  if (item.kind === 'image') {
    return (
      <img
        src={assetUrl(item.url)}
        alt={item.alt_text || ''}
        className="h-14 w-14 shrink-0 rounded-lg object-cover"
      />
    );
  }
  if (item.kind === 'video') {
    return (
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-black">
        <video src={assetUrl(item.url)} muted preload="metadata" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center text-white">▶</span>
      </div>
    );
  }
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-maroon-800 to-maroon-950 text-lg text-cream-100">
      ▶
    </div>
  );
}
